/**
 * Series picker (THEME.md §7.13; product page spec S1–S5; T-78, T-139): the element of
 * `blocks/okt-product-series.liquid`.
 *
 * - The label line names the hovered or focused tile, and the current product otherwise. Its label
 *   ("Color:") stays for a colour value (`data-okt-colour`) and gives way to a title.
 * - "+N" shows the hidden tiles in place, then gives way to them: focus moves to the first of them.
 * - Size carry-over (S4): when the variant picker of the same product selects a size, each tile links
 *   to its product's variant with that size (`data-okt-sizes`), or to the product when it has none.
 *   The block renders the links for the variant in the URL, so this only follows later changes. It
 *   listens on the section, or on Horizon's quick add window, which has none (T-163).
 * - A product with more than one colour shows its colour option in the variant picker: the line and the
 *   current tile's name follow the chosen colour (`data-colour-option`), as on the live theme.
 */
import { StandardEvents } from '@shopify/events';

const TILE = '[data-okt-series-tile]';

class OktProductSeries extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('pointerover', this.#handlePointerOver, { signal });
    this.addEventListener('pointerleave', this.#handlePointerLeave, { signal });
    this.addEventListener('focusin', this.#handleFocusIn, { signal });
    this.addEventListener('focusout', this.#handleFocusOut, { signal });
    this.addEventListener('click', this.#handleClick, { signal });
    const scope = this.closest('.shopify-section, dialog');
    scope?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /** @returns {HTMLElement | null} */
  get #name() {
    return this.querySelector('[data-okt-series-name]');
  }

  /** The current product's name, as rendered */
  #currentName = '';

  /**
   * Shows a tile's name in the label line, or the current product's without a tile.
   * @param {Element | null | undefined} tile
   */
  #showName(tile) {
    const name = this.#name;
    if (!name) return;

    if (!this.#currentName) this.#currentName = name.textContent ?? '';

    const text = tile instanceof HTMLElement ? tile.dataset.oktName ?? '' : this.#currentName;
    if (name.textContent !== text) name.textContent = text;

    const label = this.querySelector('[data-okt-series-label]');
    if (label instanceof HTMLElement) label.hidden = tile instanceof HTMLElement && !('oktColour' in tile.dataset);
  }

  /** @param {PointerEvent} event */
  #handlePointerOver = (event) => {
    const tile = event.target instanceof Element ? event.target.closest(TILE) : null;
    if (tile) this.#showName(tile);
  };

  #handlePointerLeave = () => {
    const focused = document.activeElement?.closest(TILE);
    this.#showName(focused && this.contains(focused) ? focused : null);
  };

  /** @param {FocusEvent} event */
  #handleFocusIn = (event) => {
    const tile = event.target instanceof Element ? event.target.closest(TILE) : null;
    if (tile) this.#showName(tile);
  };

  /** @param {FocusEvent} event */
  #handleFocusOut = (event) => {
    const next = event.relatedTarget instanceof Element ? event.relatedTarget.closest(TILE) : null;
    if (!next || !this.contains(next)) this.#showName(this.matches(':hover') ? this.querySelector(`${TILE}:hover`) : null);
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const more = event.target instanceof Element ? event.target.closest('[data-okt-series-more]') : null;
    if (!(more instanceof HTMLElement)) return;

    const hidden = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.okt-series__item[hidden]')]);

    more.setAttribute('aria-expanded', 'true');
    for (const item of hidden) item.hidden = false;

    const moreItem = more.closest('li');
    if (moreItem) moreItem.hidden = true;

    hidden[0]?.querySelector('a')?.focus();
  };

  /** @param {Event} event */
  #handleProductSelect = (event) => {
    const { product, selectedOptions } = /** @type {any} */ (event);
    const productId = String(product?.id ?? '').split('/').pop();
    if (productId !== this.dataset.productId || !Array.isArray(selectedOptions)) return;

    this.#followColour(selectedOptions);

    const sizeOption = this.dataset.sizeOption;
    if (!sizeOption) return;

    const option = selectedOptions.find((/** @type {{ name: string }} */ selected) => selected.name === sizeOption);
    if (!option) return;

    const size = String(option.value).trim().toLowerCase();

    for (const tile of /** @type {NodeListOf<HTMLAnchorElement>} */ (this.querySelectorAll(TILE))) {
      const { oktUrl = '', oktSizes = '{}' } = tile.dataset;

      /** @type {Record<string, number>} */
      let sizes = {};
      try {
        sizes = JSON.parse(oktSizes);
      } catch {
        sizes = {};
      }

      const variantId = sizes[size];
      const url = new URL(oktUrl, window.location.origin);
      if (variantId) url.searchParams.set('variant', String(variantId));

      tile.setAttribute('href', `${url.pathname}${url.search}`);
    }
  };

  /**
   * The chosen colour becomes the current product's name: in the line and on its tile.
   * @param {{ name: string, value: string }[]} selectedOptions
   */
  #followColour(selectedOptions) {
    const { colourOption } = this.dataset;
    const option = colourOption ? selectedOptions.find((selected) => selected.name === colourOption) : null;
    const name = this.#name;
    if (!option || !name) return;

    if (!this.#currentName) this.#currentName = name.textContent ?? '';
    const colour = String(option.value);
    if (colour === this.#currentName) return;

    const tile = this.querySelector(`${TILE}[aria-current="page"]`);
    if (tile instanceof HTMLElement) {
      tile.dataset.oktName = colour;
      const label = tile.querySelector('.visually-hidden');
      const text = label?.textContent ?? '';
      if (label && text.startsWith(this.#currentName)) label.textContent = colour + text.slice(this.#currentName.length);
    }

    this.#currentName = colour;
    this.#showName(this.querySelector(`${TILE}:hover, ${TILE}:focus`));
  }
}

if (!customElements.get('okt-product-series')) {
  customElements.define('okt-product-series', OktProductSeries);
}
